import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "@shared/dayjs";
import DatePicker from "@shared/v2/DatePicker";
import Dropdown from "@shared/v2/Dropdown";
import Modal from "@shared/v2/Modal";
import TextInput from "@shared/v2/TextInput";
import Button from "@shared/v2/Button";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";

const validateForm = (formData) => {
  const errors = {};
  if (!formData.goalTypeId) errors.goalTypeId = "Must select goal type";
  if (!formData.amount) errors.amount = "Must choose an amount";
  if (!formData.activityDate) errors.activityDate = "Must choose a date";
  if (!errors.amount && formData.amount < 1) errors.amount = "Must choose amount greater than 0";
  return errors;
};

const NewLeadGenActivityModal = ({
  agentList,
  userUuids,
  goalTypes,
  open,
  openForType = null,
  onClose,
  onSubmit,
}) => {
  const agentOptions = useMemo(() => agentList.filter((a) => a.value !== "all"), [agentList]);
  const currentUser = useCurrentUser();
  const [errors, setErrors] = useState({});
  const [goalType, setGoalType] = useState(null);
  const [activityDate, setActivityDate] = useState(dayjs().format("MM/DD/YYYY"));
  const [selectedUser, setSelectedUser] = useState();
  useEffect(() => {
    setSelectedUser(
      userUuids.length === 1 && userUuids[0] !== "all"
        ? agentList.find((a) => a.meta?.userUuid === userUuids[0])
        : agentList.find((a) => a.meta?.userUuid === currentUser.uuid),
    );
  }, [userUuids]);
  const goalTypeOptions = useMemo(
    () =>
      goalTypes?.map((type) => ({
        label: type.displayName,
        value: type.id,
        meta: type,
      })) || [],
    [goalTypes],
  );

  useEffect(() => {
    if (openForType) setGoalType(goalTypeOptions.find((gt) => String(gt.value) === openForType));
  }, [openForType, goalTypeOptions]);

  const resetForm = () => {
    setGoalType(null);
  };

  const closeModal = () => {
    resetForm();
    onClose();
  };

  const parsedActivityDate = activityDate ? dayjs(activityDate, "MM/DD/YYYY") : null;
  const serializedActivityDate = parsedActivityDate?.isValid() ? parsedActivityDate.toISOString() : "";

  return (
    <Modal
      show={Boolean(openForType) || open}
      onHide={closeModal}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[640px] tw-max-h-[90vh] tw-w-full"
    >
      <form
        className="tw-flex tw-flex-col tw-gap-[32px]"
        onSubmit={(e) => {
          e.preventDefault();
          const formData = Object.fromEntries(new FormData(e.target));
          if (!formData.noteContent.trim()) delete formData.noteContent;
          const formErrors = validateForm(formData);
          if (Object.keys(formErrors).length > 0) setErrors(formErrors);
          else onSubmit(formData).then(closeModal);
        }}
      >
        <Modal.Header title="New Activity" closeButton />

        <Modal.Body className="tw-flex tw-flex-col tw-gap-[24px] tw-overflow-auto">
          <div className="tw-flex tw-flex-wrap tw-gap-[24px]">
            <Dropdown
              containerClassName="tw-flex-1"
              label="Type"
              isRequired
              name="goalTypeId"
              value={goalType}
              error={errors.goalTypeId}
              menuShouldComeToFront
              onChange={(type) => {
                setErrors(({ goalTypeId, ...currentErrors }) => currentErrors);
                setGoalType(type);
              }}
              options={goalTypeOptions}
            />
            {currentUser.account.isAdmin && (
              <>
                <Dropdown
                  label="For User"
                  containerClassName="tw-flex-1"
                  options={agentOptions}
                  menuShouldComeToFront
                  value={selectedUser}
                  onChange={setSelectedUser}
                  isSearchable
                />
                {selectedUser && <input type="hidden" name="userUuid" value={selectedUser?.meta?.userUuid} />}
              </>
            )}
          </div>

          <div className="tw-flex tw-flex-wrap tw-gap-[24px]">
            <div className="tw-flex tw-flex-1 tw-flex-col tw-grow">
              <DatePicker
                className="tw-basis-[40%] tw-grow"
                value={activityDate}
                onChange={(date) => {
                  setActivityDate(date);
                  setErrors(({ activityDate: _, ...currentErrors }) => currentErrors);
                }}
                inputProps={{ size: 9, label: "Date", isRequired: true, error: errors.activityDate }}
              />
              <input type="hidden" name="activityDate" value={serializedActivityDate} />
            </div>
            <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-[4px]">
              <TextInput
                isRequired
                label={`# ${goalType?.label ? `of ${goalType.label}` : ""}`}
                error={errors.amount}
                className="tw-min-w-[150px] tw-w-full"
                name="amount"
                defaultValue={1}
                type="number"
                onKeyDown={(e) => {
                  if (/[-.]/.test(e.key)) e.preventDefault();
                }}
                onChange={() => setErrors(({ amount, ...currentErrors }) => currentErrors)}
              />
            </div>
          </div>

          <TextInput label="Notes" multiline name="noteContent" />
        </Modal.Body>

        <Modal.Footer className="tw-flex tw-justify-between tw-gap-[16px]">
          <Button schema="tertiary" size="medium" onClick={closeModal}>
            Cancel
          </Button>
          <Button type="submit" schema="primary" size="medium">
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

NewLeadGenActivityModal.propTypes = {
  agentList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  userUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
  goalTypes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  open: PropTypes.bool.isRequired,
  openForType: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default NewLeadGenActivityModal;
